import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api, { errMsg } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, Card, Btn, inputCls, Badge } from "../components/common";
import { Send, MessageSquare, Eye } from "lucide-react";

export default function Chat() {
  const { user } = useAuth();
  const isViewer = ["Super Admin", "Direktur"].includes(user?.role);
  const [tab, setTab] = useState("chat");
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [convs, setConvs] = useState([]);
  const [supThread, setSupThread] = useState(null);
  const [supMsgs, setSupMsgs] = useState([]);
  const [taskForm, setTaskForm] = useState(null);
  const bottomRef = useRef(null);

  const loadContacts = useCallback(() => {
    api.get("/chat/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, []);
  const loadThread = useCallback((uid) => {
    if (uid) api.get(`/chat/thread/${uid}`).then((r) => setThread(r.data)).catch(() => {});
  }, []);
  const loadConvs = useCallback(() => {
    if (isViewer) api.get("/chat/supervise/conversations").then((r) => setConvs(r.data)).catch(() => {});
  }, [isViewer]);

  useEffect(() => { loadContacts(); loadConvs(); }, [loadContacts, loadConvs]);
  useEffect(() => {
    const t = setInterval(() => { loadContacts(); if (active) loadThread(active.id); }, 5000);
    return () => clearInterval(t);
  }, [active, loadContacts, loadThread]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const openChat = (c) => { setActive(c); setThread([]); loadThread(c.id); };

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      await api.post("/chat/send", { recipient_id: active.id, text });
      setText("");
      loadThread(active.id);
      loadContacts();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openSup = async (c) => {
    setSupThread(c);
    try {
      const r = await api.get("/chat/supervise/thread", { params: { a: c.ids[0], b: c.ids[1] } });
      setSupMsgs(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendTask = async () => {
    if (!taskForm?.title?.trim()) return toast.error("Judul tugas wajib diisi");
    try {
      await api.post("/chat/send-task", { recipient_id: active.id, title: taskForm.title, due_date: taskForm.due_date || null, note: taskForm.note || "" });
      toast.success("Tugas terkirim — masuk ke To Do List & kalender user tersebut");
      setTaskForm(null);
      loadThread(active.id);
      loadContacts();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const bubble = (m, mine, key) => (
    <div key={key} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
      <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${mine ? "bg-emerald-600/20 text-emerald-100 border border-emerald-500/30" : "bg-slate-800 text-slate-200 border border-slate-700"}`}>
        <div>{m.text}</div>
        <div className="text-[9px] font-mono text-slate-500 mt-0.5 text-right">{(m.created_at || "").slice(5, 16).replace("T", " ")}</div>
      </div>
    </div>
  );

  return (
    <div data-testid="chat-page">
      <PageHeader title="Chat Internal" subtitle="Pesan 1:1 antar pengguna" testid="chat-header" />
      {isViewer && (
        <div className="flex gap-2 mb-4 flex-wrap">
          {[["chat", "Percakapan Saya"], ["supervise", "Semua Percakapan (Pemantauan)"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`chat-tab-${k}`}
              className={`text-xs sm:text-sm px-3.5 py-2 rounded-lg border transition-colors ${tab === k ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900 hover:text-slate-200"}`}>
              {l}
            </button>
          ))}
        </div>
      )}

      {tab === "chat" && (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
          <Card className="!p-2" testid="chat-contacts">
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {contacts.map((c) => (
                <button key={c.id} onClick={() => openChat(c)} data-testid={`chat-contact-${c.id}`}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${active?.id === c.id ? "bg-emerald-600/10 border border-emerald-500/40" : "hover:bg-slate-800 border border-transparent"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-slate-200 truncate">{c.name}</span>
                    {c.unread > 0 && <span className="text-[10px] font-mono bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-full px-2 py-0.5" data-testid={`chat-unread-${c.id}`}>{c.unread}</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{c.role}{c.last_message ? ` · ${c.last_message.slice(0, 32)}` : ""}</div>
                </button>
              ))}
              {contacts.length === 0 && <p className="text-slate-500 text-sm p-3">Belum ada pengguna lain.</p>}
            </div>
          </Card>

          <Card className="!p-0 flex flex-col" testid="chat-panel">
            {!active ? (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-600 py-16">
                <MessageSquare className="w-8 h-8 mb-2" />
                <p className="text-sm">Pilih pengguna untuk mulai percakapan</p>
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-200" data-testid="chat-active-name">{active.name}</span>
                  <Badge tone="slate">{active.role}</Badge>
                  <button onClick={() => setTaskForm(taskForm ? null : { title: "", due_date: "", note: "" })}
                    className="ml-auto text-[11px] text-emerald-400 hover:text-emerald-300 border border-emerald-500/40 rounded-lg px-2 py-1 transition-colors"
                    data-testid="chat-give-task-button">+ Beri Tugas</button>
                </div>
                {taskForm && (
                  <div className="px-3 py-2.5 border-b border-slate-800 bg-slate-950/60 grid grid-cols-1 sm:grid-cols-[1fr_150px_auto] gap-2" data-testid="chat-task-form">
                    <input className={inputCls} placeholder="Judul tugas (wajib)..." value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} data-testid="chat-task-title" />
                    <input type="date" className={inputCls} value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} data-testid="chat-task-due" title="Deadline" />
                    <Btn onClick={sendTask} data-testid="chat-task-send">Kirim Tugas</Btn>
                    <input className={inputCls + " sm:col-span-3"} placeholder="Catatan tambahan (opsional)..." value={taskForm.note} onChange={(e) => setTaskForm({ ...taskForm, note: e.target.value })} data-testid="chat-task-note" />
                  </div>
                )}
                <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[55vh] min-h-[300px]" data-testid="chat-thread">
                  {thread.map((m) => bubble(m, m.sender_id === user?.id, m.id))}
                  {thread.length === 0 && <p className="text-slate-600 text-xs text-center py-8">Belum ada pesan. Sapa duluan!</p>}
                  <div ref={bottomRef} />
                </div>
                <div className="p-3 border-t border-slate-800 flex gap-2">
                  <input className={inputCls} placeholder="Tulis pesan..." value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    data-testid="chat-input" />
                  <Btn onClick={send} data-testid="chat-send-button"><Send className="w-4 h-4" /></Btn>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {tab === "supervise" && isViewer && (
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4">
          <Card className="!p-2" testid="chat-supervise-list">
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {convs.map((c, i) => (
                <button key={i} onClick={() => openSup(c)} data-testid={`sup-conv-${i}`}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${supThread === c ? "bg-amber-600/10 border border-amber-500/40" : "hover:bg-slate-800 border border-transparent"}`}>
                  <div className="text-sm text-slate-200">{c.a} &harr; {c.b}</div>
                  <div className="text-[11px] text-slate-500 truncate">{c.count} pesan · {(c.last || "").slice(0, 36)}</div>
                </button>
              ))}
              {convs.length === 0 && <p className="text-slate-500 text-sm p-3">Belum ada percakapan antar pengguna.</p>}
            </div>
          </Card>
          <Card className="!p-0" testid="chat-supervise-panel">
            {!supThread ? (
              <div className="flex flex-col items-center justify-center text-slate-600 py-16">
                <Eye className="w-8 h-8 mb-2" />
                <p className="text-sm">Pilih percakapan untuk melihat isinya (mode pantau read-only)</p>
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-slate-800 text-sm text-slate-200">{supThread.a} &harr; {supThread.b}</div>
                <div className="overflow-y-auto p-4 space-y-2 max-h-[55vh]" data-testid="sup-thread">
                  {supMsgs.map((m) => bubble(m, m.sender_id === supThread.ids[0], m.id))}
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
